import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ModelConfigsInline } from '../components/models/ModelConfigsInline';
import PageHeader from '../components/layout/PageHeader';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { FormCheckbox, FormField, FormSelect } from '../components/shared/form';
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from '../components/modelcatalog/CatalogDialog';
import { ADD_MODEL_STATUS_OPTIONS } from '../components/modelcatalog/AddModelDialog';
import { catalogFormErrors, type CatalogFormErrors } from '../components/modelcatalog/catalogErrors';
import { SuccessorSelect } from '../components/modelcatalog/SuccessorSelect';
import { formatCheckedAgo, formatDay } from '../components/modelcatalog/review/reviewUtils';
import { useFlag } from '../hooks/useFeatureFlags';
import { useModel, useModels, useUpdateModel } from '../hooks/useModels';
import { useOptionalToast } from '../contexts/ToastContext';
import { formatPricePerMTok, formatTokenCount } from '../utils/formatUtils';
import type { Model, ModelPricingTier, ModelStatus, UpdateModelInput } from '../types';

/**
 * The Model page (task 1093): one catalog Model row — its provider facts,
 * prices, pricing tiers and lifecycle (status, shutdown date, last checked by
 * the catalog review). Platform admins holding `innovate.fe.model-catalog.manage`
 * can edit it, including setting the status back to `active`; `?edit=1` (the
 * catalog review's Edit) opens the page directly in edit mode.
 *
 * A price is what metering charges every later call, for every account, so a
 * changed price field carries a one-line note saying so. Saving a change to
 * `retired` asks first, naming the Model's configs count (task 1104), with the
 * same copy as the catalog review's Mark retired confirm.
 *
 * The Successor (task 1130) is the Model the resolver runs on instead once
 * this one is withdrawn; the facts name it and the edit state picks it.
 */

const METERING_NOTE = 'Applies to every subsequent call for every account; past usage is not recalculated.';

const STATUS_BADGE: Record<ModelStatus, string> = {
  active: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
  deprecated: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
  retired: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

interface TierDraft {
  over: string;
  inputPriceMTok: string;
  outputPriceMTok: string;
}

interface ModelDraft {
  displayName: string;
  contextWindow: string;
  maxOutputTokens: string;
  inputPriceMTok: string;
  outputPriceMTok: string;
  cacheReadPriceMTok: string;
  cacheWritePriceMTok: string;
  tiers: TierDraft[];
  supportsTemperature: boolean;
  status: ModelStatus;
  shutdownDate: string;
  successorModelId: string;  // '' = none
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const priceText = (value: string | number | null | undefined) => (value === null || value === undefined ? '' : String(value));

const draftFrom = (model: Model): ModelDraft => ({
  displayName: model.displayName ?? '',
  contextWindow: String(model.contextWindow),
  maxOutputTokens: String(model.maxOutputTokens),
  inputPriceMTok: priceText(model.inputPriceMTok),
  outputPriceMTok: priceText(model.outputPriceMTok),
  cacheReadPriceMTok: priceText(model.cacheReadPriceMTok),
  cacheWritePriceMTok: priceText(model.cacheWritePriceMTok),
  tiers: (Array.isArray(model.pricingTiers) ? model.pricingTiers : []).map((t) => ({
    over: String(t.over),
    inputPriceMTok: String(t.inputPriceMTok),
    outputPriceMTok: String(t.outputPriceMTok),
  })),
  supportsTemperature: model.supportsTemperature !== false,
  status: model.status,
  shutdownDate: model.shutdownDate ? formatDay(model.shutdownDate) : '',
  successorModelId: model.successorModelId ?? '',
});

/** Two price texts are the same price when both are blank or both parse to the same number. */
const samePrice = (a: string, b: string) =>
  a.trim() === b.trim() || (a.trim() !== '' && b.trim() !== '' && Number(a) === Number(b));

const sameTiers = (a: TierDraft[], b: TierDraft[]) =>
  a.length === b.length &&
  a.every(
    (t, i) =>
      Number(t.over) === Number(b[i].over) &&
      samePrice(t.inputPriceMTok, b[i].inputPriceMTok) &&
      samePrice(t.outputPriceMTok, b[i].outputPriceMTok),
  );

const isWhole = (v: string) => /^\d+$/.test(v.trim()) && Number(v) > 0;
const isPrice = (v: string) => v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= 0;

function validate(draft: ModelDraft): Record<string, string> {
  const fields: Record<string, string> = {};
  if (!draft.displayName.trim()) fields.displayName = 'Display name is required';
  if (!isWhole(draft.contextWindow)) fields.contextWindow = 'Enter a whole number of tokens';
  if (!isWhole(draft.maxOutputTokens)) fields.maxOutputTokens = 'Enter a whole number of tokens';
  if (!isPrice(draft.inputPriceMTok)) fields.inputPriceMTok = 'Enter a price of 0 or more';
  if (!isPrice(draft.outputPriceMTok)) fields.outputPriceMTok = 'Enter a price of 0 or more';
  if (draft.cacheReadPriceMTok.trim() && !isPrice(draft.cacheReadPriceMTok)) fields.cacheReadPriceMTok = 'Enter a price of 0 or more, or leave blank';
  if (draft.cacheWritePriceMTok.trim() && !isPrice(draft.cacheWritePriceMTok)) fields.cacheWritePriceMTok = 'Enter a price of 0 or more, or leave blank';
  if (draft.tiers.some((t) => !isWhole(t.over) || !isPrice(t.inputPriceMTok) || !isPrice(t.outputPriceMTok))) {
    fields.pricingTiers = 'Each tier needs a whole-number threshold and two prices of 0 or more';
  }
  return fields;
}

/** Only the fields the User changed; `null` clears a blanked nullable field. */
function patchFrom(draft: ModelDraft, stored: ModelDraft): UpdateModelInput {
  const patch: UpdateModelInput = {};
  if (draft.displayName.trim() !== stored.displayName) patch.displayName = draft.displayName.trim();
  if (Number(draft.contextWindow) !== Number(stored.contextWindow)) patch.contextWindow = Number(draft.contextWindow);
  if (Number(draft.maxOutputTokens) !== Number(stored.maxOutputTokens)) patch.maxOutputTokens = Number(draft.maxOutputTokens);
  if (!samePrice(draft.inputPriceMTok, stored.inputPriceMTok)) patch.inputPriceMTok = draft.inputPriceMTok.trim();
  if (!samePrice(draft.outputPriceMTok, stored.outputPriceMTok)) patch.outputPriceMTok = draft.outputPriceMTok.trim();
  if (!samePrice(draft.cacheReadPriceMTok, stored.cacheReadPriceMTok)) {
    patch.cacheReadPriceMTok = draft.cacheReadPriceMTok.trim() || null;
  }
  if (!samePrice(draft.cacheWritePriceMTok, stored.cacheWritePriceMTok)) {
    patch.cacheWritePriceMTok = draft.cacheWritePriceMTok.trim() || null;
  }
  if (!sameTiers(draft.tiers, stored.tiers)) {
    const tiers: ModelPricingTier[] = draft.tiers
      .map((t) => ({ over: Number(t.over), inputPriceMTok: Number(t.inputPriceMTok), outputPriceMTok: Number(t.outputPriceMTok) }))
      .sort((a, b) => a.over - b.over);
    if (tiers.length > 0) {
      patch.pricingTiers = tiers;
    } else {
      // The admin removed every tier: the service clears tiers only with the explicit flag (task 1277).
      patch.pricingTiers = null;
      patch.clearPricingTiers = true;
    }
  }
  if (draft.supportsTemperature !== stored.supportsTemperature) patch.supportsTemperature = draft.supportsTemperature;
  if (draft.status !== stored.status) patch.status = draft.status;
  if (draft.shutdownDate !== stored.shutdownDate) patch.shutdownDate = draft.shutdownDate || null;
  if (draft.successorModelId !== stored.successorModelId) patch.successorModelId = draft.successorModelId || null;
  return patch;
}

function MeteringNote({ show, testId }: { show: boolean; testId: string }) {
  if (!show) return null;
  return (
    <p data-testid={testId} className="mt-1 text-xs text-amber-700 dark:text-amber-300">
      {METERING_NOTE}
    </p>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{children}</dd>
    </div>
  );
}

const CARD = 'bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6';
const EMPTY_ERRORS: CatalogFormErrors = { fields: {}, general: null };

function ModelEditForm({ model, catalog, onDone }: { model: Model; catalog: Model[]; onDone: () => void }) {
  const update = useUpdateModel();
  const toast = useOptionalToast();
  const [stored] = useState(() => draftFrom(model));
  const [draft, setDraft] = useState<ModelDraft>(stored);
  const [errors, setErrors] = useState<CatalogFormErrors>(EMPTY_ERRORS);
  const [confirmingRetire, setConfirmingRetire] = useState(false);

  const set = <K extends keyof ModelDraft>(key: K, value: ModelDraft[K]) => setDraft((prev) => ({ ...prev, [key]: value }));
  const setTier = (index: number, key: keyof TierDraft, value: string) =>
    set('tiers', draft.tiers.map((t, i) => (i === index ? { ...t, [key]: value } : t)));

  const patch = patchFrom(draft, stored);
  const dirty = Object.keys(patch).length > 0;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const fields = validate(draft);
    if (Object.keys(fields).length > 0) {
      setErrors({ fields, general: null });
      return;
    }
    setErrors(EMPTY_ERRORS);
    // Retiring blocks new Model Configs on this Model; confirm before the write.
    if (patch.status === 'retired') {
      setConfirmingRetire(true);
      return;
    }
    await save();
  };

  const save = async () => {
    try {
      await update.mutateAsync({ id: model.id, data: patch });
      toast?.success(`${model.modelId} updated`);
      onDone();
    } catch (error) {
      setErrors(catalogFormErrors(error, undefined, { successorField: 'successorModelId' }));
    }
  };

  const confirmRetire = async () => {
    setConfirmingRetire(false);
    await save();
  };

  return (
    <>
      <form onSubmit={handleSubmit} noValidate className={`mt-6 space-y-6 ${CARD}`} data-testid="model-edit-form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Display name" name="displayName" value={draft.displayName} onChange={(e) => set('displayName', e.target.value)} error={errors.fields.displayName} required />
          <FormSelect
            label="Status"
            name="status"
            value={draft.status}
            onChange={(e) => set('status', e.target.value as ModelStatus)}
            options={ADD_MODEL_STATUS_OPTIONS}
            error={errors.fields.status}
          />
          <FormField label="Context window (tokens)" name="contextWindow" type="number" min={1} step={1} value={draft.contextWindow} onChange={(e) => set('contextWindow', e.target.value)} error={errors.fields.contextWindow} required />
          <FormField label="Max output (tokens)" name="maxOutputTokens" type="number" min={1} step={1} value={draft.maxOutputTokens} onChange={(e) => set('maxOutputTokens', e.target.value)} error={errors.fields.maxOutputTokens} required />
          <div>
            <FormField label="Input price per 1M tokens" name="inputPriceMTok" type="number" min={0} step="any" value={draft.inputPriceMTok} onChange={(e) => set('inputPriceMTok', e.target.value)} error={errors.fields.inputPriceMTok} required />
            <MeteringNote show={!samePrice(draft.inputPriceMTok, stored.inputPriceMTok)} testId="metering-note-inputPriceMTok" />
          </div>
          <div>
            <FormField label="Output price per 1M tokens" name="outputPriceMTok" type="number" min={0} step="any" value={draft.outputPriceMTok} onChange={(e) => set('outputPriceMTok', e.target.value)} error={errors.fields.outputPriceMTok} required />
            <MeteringNote show={!samePrice(draft.outputPriceMTok, stored.outputPriceMTok)} testId="metering-note-outputPriceMTok" />
          </div>
          <div>
            <FormField label="Cache read price per 1M tokens" name="cacheReadPriceMTok" type="number" min={0} step="any" value={draft.cacheReadPriceMTok} onChange={(e) => set('cacheReadPriceMTok', e.target.value)} error={errors.fields.cacheReadPriceMTok} />
            <MeteringNote show={!samePrice(draft.cacheReadPriceMTok, stored.cacheReadPriceMTok)} testId="metering-note-cacheReadPriceMTok" />
          </div>
          <div>
            <FormField label="Cache write price per 1M tokens" name="cacheWritePriceMTok" type="number" min={0} step="any" value={draft.cacheWritePriceMTok} onChange={(e) => set('cacheWritePriceMTok', e.target.value)} error={errors.fields.cacheWritePriceMTok} />
            <MeteringNote show={!samePrice(draft.cacheWritePriceMTok, stored.cacheWritePriceMTok)} testId="metering-note-cacheWritePriceMTok" />
          </div>
          <FormField label="Shutdown date" name="shutdownDate" type="date" value={draft.shutdownDate} onChange={(e) => set('shutdownDate', e.target.value)} error={errors.fields.shutdownDate} />
          <FormCheckbox
            label="Accepts temperature"
            name="supportsTemperature"
            checked={draft.supportsTemperature}
            onChange={(e) => set('supportsTemperature', e.target.checked)}
            description="Clear this for a model whose provider rejects the temperature parameter; calls on it leave temperature out."
            error={errors.fields.supportsTemperature}
            className="sm:col-span-2"
          />
          <SuccessorSelect
            name="successorModelId"
            value={draft.successorModelId}
            onChange={(value) => set('successorModelId', value)}
            models={catalog}
            excludeId={model.id}
            error={errors.fields.successorModelId}
          />
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">Pricing tiers</legend>
          <p className="text-xs text-gray-500 dark:text-gray-400">Above the threshold of input tokens, these prices apply instead.</p>
          <div className="mt-2 space-y-2">
            {draft.tiers.map((tier, index) => (
              <div key={index} className="flex flex-wrap items-end gap-2">
                <FormField label="Over (tokens)" name={`tier-${index}-over`} type="number" min={1} step={1} value={tier.over} onChange={(e) => setTier(index, 'over', e.target.value)} />
                <FormField label="Input $/MTok" name={`tier-${index}-input`} type="number" min={0} step="any" value={tier.inputPriceMTok} onChange={(e) => setTier(index, 'inputPriceMTok', e.target.value)} />
                <FormField label="Output $/MTok" name={`tier-${index}-output`} type="number" min={0} step="any" value={tier.outputPriceMTok} onChange={(e) => setTier(index, 'outputPriceMTok', e.target.value)} />
                <button
                  type="button"
                  aria-label={`Remove tier ${index + 1}`}
                  onClick={() => set('tiers', draft.tiers.filter((_, i) => i !== index))}
                  className="mb-1 rounded p-1.5 text-gray-400 hover:text-red-600"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set('tiers', [...draft.tiers, { over: '', inputPriceMTok: '', outputPriceMTok: '' }])}
              className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline dark:text-indigo-400"
            >
              <PlusIcon className="h-4 w-4" />
              Add tier
            </button>
          </div>
          {errors.fields.pricingTiers && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.fields.pricingTiers}</p>}
          <MeteringNote show={!sameTiers(draft.tiers, stored.tiers)} testId="metering-note-pricingTiers" />
        </fieldset>

        {errors.general && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {errors.general}
          </p>
        )}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onDone} disabled={update.isPending} className={SECONDARY_BUTTON}>
            Cancel
          </button>
          <button type="submit" disabled={!dirty || update.isPending} className={PRIMARY_BUTTON}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={confirmingRetire}
        onClose={() => setConfirmingRetire(false)}
        onConfirm={confirmRetire}
        title={`Retire ${model.modelId}?`}
        message={
          <div className="space-y-2" data-testid="confirm-mark-retired">
            {model.configCount !== undefined && (
              <p className="font-medium">{plural(model.configCount, 'config', 'configs')} reference it.</p>
            )}
            <p>
              Retired Models cannot be chosen for new Model Configs and are hidden from model pickers. Existing configs keep
              working until the provider refuses requests for the model.
            </p>
            <p>You can set the status back to active on this page.</p>
          </div>
        }
        confirmLabel="Mark retired"
        confirmStyle="danger"
      />
    </>
  );
}

export default function ModelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = useFlag('innovate.fe.model-catalog.manage');
  const { data: model, isLoading, error } = useModel(id);
  // The whole catalog, retired included, so a stored successor always has a name.
  const { data: catalog } = useModels({ includeRetired: true });
  const editRequested = searchParams.get('edit') === '1';
  const [isEditing, setIsEditing] = useState(editRequested);

  // `?edit=1` arriving on an already-mounted page (e.g. a second Edit click) opens edit mode too.
  useEffect(() => {
    if (editRequested) setIsEditing(true);
  }, [editRequested]);

  const stopEditing = () => {
    setIsEditing(false);
    if (editRequested) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('edit');
          return next;
        },
        { replace: true },
      );
    }
  };

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">Loading Model...</div>;
  }
  if (error || !model) {
    return <div className="flex h-64 items-center justify-center text-red-500 dark:text-red-400">Error loading Model. Please try again.</div>;
  }

  const tiers = Array.isArray(model.pricingTiers) ? model.pricingTiers : [];
  const editing = isEditing && canManage;
  const successor = model.successorModelId ? catalog?.find((m) => m.id === model.successorModelId) : undefined;

  return (
    <div>
      <PageHeader
        title={model.displayName ?? model.modelId}
        description={`${model.provider?.key ?? 'Model'} · ${model.modelId}`}
        actions={
          canManage && !editing ? (
            <button type="button" onClick={() => setIsEditing(true)} className={SECONDARY_BUTTON}>
              <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
              Edit
            </button>
          ) : undefined
        }
      />
      <div className="mt-2 text-sm">
        <Link
          to={`/model-catalog?model=${encodeURIComponent(model.id)}`}
          className="text-indigo-600 hover:underline dark:text-indigo-400"
        >
          Models
        </Link>
        {model.configCount !== undefined && (
          <>
            <span className="text-gray-400"> · </span>
            <span className="text-gray-600 dark:text-gray-300">
              {plural(model.configCount, 'Model Config', 'Model Configs')}
            </span>
          </>
        )}
      </div>

      {editing ? (
        <ModelEditForm model={model} catalog={catalog ?? []} onDone={stopEditing} />
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className={CARD}>
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Provider facts</h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Fact label="Model ID">
                <span className="font-mono">{model.modelId}</span>
                {model.resolvedModelId && model.resolvedModelId !== model.modelId && (
                  <span className="block font-mono text-xs text-gray-500 dark:text-gray-400">→ {model.resolvedModelId}</span>
                )}
              </Fact>
              <Fact label="Provider">
                <span className="font-mono">{model.provider?.key ?? '—'}</span>
              </Fact>
              <Fact label="Context window">
                {model.contextWindow.toLocaleString('en-US')} ({formatTokenCount(model.contextWindow)})
              </Fact>
              <Fact label="Max output">{model.maxOutputTokens.toLocaleString('en-US')}</Fact>
              <Fact label="Input / output $/MTok">
                {formatPricePerMTok(model.inputPriceMTok)} / {formatPricePerMTok(model.outputPriceMTok)}
              </Fact>
              <Fact label="Cache read / write $/MTok">
                {formatPricePerMTok(model.cacheReadPriceMTok)} / {formatPricePerMTok(model.cacheWritePriceMTok)}
              </Fact>
              <Fact label="Pricing tiers">
                {tiers.length === 0 ? (
                  '—'
                ) : (
                  <ul className="space-y-0.5">
                    {tiers.map((tier) => (
                      <li key={tier.over}>
                        Over {formatTokenCount(tier.over)}: {formatPricePerMTok(tier.inputPriceMTok)} in · {formatPricePerMTok(tier.outputPriceMTok)} out
                      </li>
                    ))}
                  </ul>
                )}
              </Fact>
              <Fact label="Family">{model.family ?? '—'}</Fact>
              <Fact label="Temperature">
                <span data-testid="model-detail-temperature">
                  {model.supportsTemperature === false ? 'Not accepted (calls leave it out)' : 'Accepted'}
                </span>
              </Fact>
            </dl>
          </div>
          <div className={CARD}>
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Lifecycle</h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Fact label="Status">
                <span data-testid="model-detail-status" className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[model.status]}`}>
                  {model.status}
                </span>
              </Fact>
              <Fact label="Shutdown date">{model.shutdownDate ? formatDay(model.shutdownDate) : '—'}</Fact>
              <Fact label="Successor">
                <span data-testid="model-detail-successor">
                  {model.successorModelId ? (
                    <Link to={`/model-catalog/models/${model.successorModelId}`} className="text-indigo-600 hover:underline dark:text-indigo-400">
                      {successor ? successor.displayName ?? successor.modelId : model.successorModelId}
                    </Link>
                  ) : (
                    '—'
                  )}
                </span>
              </Fact>
              <Fact label="Released">{model.releasedAt ? formatDay(model.releasedAt) : '—'}</Fact>
              <Fact label="Last checked">
                {model.lastSyncedAt ? (
                  <time dateTime={model.lastSyncedAt} title={new Date(model.lastSyncedAt).toLocaleString()}>
                    {formatCheckedAgo(model.lastSyncedAt)}
                  </time>
                ) : (
                  'never'
                )}
              </Fact>
            </dl>
          </div>
        </div>
      )}

      {/* The configs that run on this Model, inline (task 1167) — the same
          section the Models page shows under an expanded Model, in place of
          the link out to the retired Model Configs page. */}
      <div className="mt-6">
        <ModelConfigsInline model={model} variant="card" />
      </div>
    </div>
  );
}
